// A collapsible section, or a tab, that holds unsaved changes (TGCLOUD-1902,
// rule 5): how many, beside its heading, so a change hidden inside a collapsed
// section is still in view. Text on a gold edge, not a dot: never a colour
// alone (rule 3).

import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { changedPalette } from 'src/theme/palette';

/**
 * @param {object} props
 * @param {number} [props.count]  the changes inside; none known draws "Changed"
 * @param {React.ReactNode} [props.section]  the section's name, for the hover title
 */
export default function ChangedSectionMarker({ count, section, sx }) {
  const t = useAutoTranslation();
  const palette = changedPalette(useTheme());
  const known = Number.isFinite(count) && count > 0;
  const text = !known
    ? t('Changed')
    : count === 1
    ? t('1 change')
    : t('{{count}} changes', { count });
  // Its text is read as part of the section's heading ("Person, 2 changes"),
  // so it carries no label of its own: a label on a span is not announced.
  const title =
    typeof section === 'string' && section
      ? t('Unsaved changes in {{section}}', { section })
      : t('Unsaved changes');
  return (
    <Box
      component="span"
      title={title}
      data-changed-section-marker=""
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          flexShrink: 0,
          ml: 0.5,
          px: 0.75,
          borderRadius: 1,
          border: `1px solid ${palette.main}`,
          borderLeftWidth: 3,
          bgcolor: palette.bg,
          color: palette.text,
          fontSize: '0.7rem',
          fontWeight: 600,
          lineHeight: '1.1rem',
          whiteSpace: 'nowrap'
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
      ]}
    >
      {text}
    </Box>
  );
}
